import { memo, useCallback } from 'react'

import { Divider, SvgIcon } from '@mui/material'
import { OrgEnvironmentRoleRamMember } from '@nativewaves/nw-cloud-sdk/org'
import { PlugDisconnected } from 'emotion-icons/fluentui-system-filled'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemConfirm, MenuItemCopyId } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useWorkspaceMemberDeleteMutation } from 'hooks/mutations/smep'
import { workspaceSettingsPermissionsRoute } from 'pages/NWPlatform/Workspaces/Workspace/Settings'
import { resolveDisplayName } from 'utils/userInfo'

const RowActionsRaw = ({ row, onClose }: RowActionsProps<OrgEnvironmentRoleRamMember>) => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { workspaceId } = workspaceSettingsPermissionsRoute.useParams()

  const { id, memberType, teamInfo, userInfo } = row.original
  const derivedMemberInfo = memberType === 'User' ? userInfo! : teamInfo!

  const { mutate: deleteMember } = useWorkspaceMemberDeleteMutation()

  const handleDeleteMember = useCallback(() => {
    onClose()

    deleteMember(
      { workspaceId, memberId: id },
      {
        onSuccess: () =>
          enqueueSnackbar(
            t('domain:Workspace.Dashboard.nameDelete', { name: resolveDisplayName(userInfo) || teamInfo?.name }),
            {
              variant: 'success',
            },
          ),
      },
    )
  }, [onClose, deleteMember, workspaceId, id, enqueueSnackbar, t, userInfo, teamInfo?.name])

  return (
    <>
      <MenuItemCopyId onSuccess={onClose} id={derivedMemberInfo.id} />
      <Divider sx={{ my: 1 }} component="li" />
      <MenuItemConfirm
        onConfirm={handleDeleteMember}
        staticLabel={t('domain:Workspace.Dashboard.removeMember')}
        startIcon={
          <SvgIcon color="error">
            <PlugDisconnected />
          </SvgIcon>
        }
        ConfirmTypographyProps={{ color: 'error' }}
      />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
